export type TimeframeUiState = {
  expanded: boolean;
  indicators: {
    expanded: boolean;
    details: {
      expanded: boolean;
    };
  };
  strategy: {
    expanded: boolean;
    long: {
      expanded: boolean;
      entries: {
        expanded: boolean;
        details: {
          expanded: boolean;
        };
      };
      exits: {
        expanded: boolean;
        details: {
          expanded: boolean;
        };
      };
    };
    short: {
      expanded: boolean;
      entries: {
        expanded: boolean;
        details: {
          expanded: boolean;
        };
      };
      exits: {
        expanded: boolean;
        details: {
          expanded: boolean;
        };
      };
    };
  };
};

export type TradingFormUIState = {
  sections: {
    market: {
      expanded: boolean;
    };
    timeframes: {
      expanded: boolean;
      items: TimeframeUiState[];
    };
    positionManagement: {
      expanded: boolean;
    };
    tradingSchedule: {
      expanded: boolean;
    };
  };
};

export type PersistedTradingFormUIState = Pick<TradingFormUIState, 'sections'>;

const normalizeExpandable = (node?: { expanded?: boolean }) => ({
  expanded: node?.expanded ?? true
});

const normalizeStrategyDirection = (
  direction?: Partial<TimeframeUiState['strategy']['long']>
): TimeframeUiState['strategy']['long'] => ({
  expanded: direction?.expanded ?? true,
  // Strategy entries/exits contain nested "Configured signals" accordion state.
  entries: {
    expanded: direction?.entries?.expanded ?? true,
    details: normalizeExpandable(direction?.entries?.details)
  },
  exits: {
    expanded: direction?.exits?.expanded ?? true,
    details: normalizeExpandable(direction?.exits?.details)
  }
});

const normalizeTimeframeUiState = (
  timeframe?: Partial<TimeframeUiState>
): TimeframeUiState => ({
  expanded: timeframe?.expanded ?? true,
  indicators: {
    expanded: timeframe?.indicators?.expanded ?? true,
    details: normalizeExpandable(timeframe?.indicators?.details)
  },
  strategy: {
    expanded: timeframe?.strategy?.expanded ?? true,
    long: normalizeStrategyDirection(timeframe?.strategy?.long),
    short: normalizeStrategyDirection(timeframe?.strategy?.short)
  }
});

export const defaultTradingFormUIState: TradingFormUIState = {
  sections: {
    market: { expanded: true },
    timeframes: {
      expanded: true,
      items: []
    },
    positionManagement: { expanded: true },
    tradingSchedule: { expanded: true }
  }
};

export const normalizeTradingFormUIState = (
  uiState?: Partial<TradingFormUIState>
): TradingFormUIState => ({
  sections: {
    market: normalizeExpandable(uiState?.sections?.market),
    timeframes: {
      expanded: uiState?.sections?.timeframes?.expanded ?? true,
      items: (uiState?.sections?.timeframes?.items ?? []).map((timeframe) =>
        normalizeTimeframeUiState(timeframe)
      )
    },
    positionManagement: normalizeExpandable(
      uiState?.sections?.positionManagement
    ),
    tradingSchedule: normalizeExpandable(uiState?.sections?.tradingSchedule)
  }
});

export const toPersistedTradingFormUIState = (
  uiState: TradingFormUIState
): PersistedTradingFormUIState => ({
  sections: uiState.sections
});
